import { FileX2 } from "lucide-react";
import { clsx } from "clsx";
import { Badge } from "@/components/ui/Badge";
import {
  CONTRACT_STATUS_LABELS,
  PROJECT_STATUS_COLORS,
  PROJECT_STATUS_LABELS,
  type Project,
  type ProjectContractStatus,
  type ProjectStatus,
} from "../types";

/** Data ISO → dd.mm.rrrr (bez strefy: pole to czysta data). */
function fmtDate(iso: string | null | undefined): string {
  if (!iso) return "";
  const [y, m, d] = iso.split("-");
  return d && m && y ? `${d}.${m}.${y}` : iso;
}

function contractTitle(status: ProjectContractStatus, terminatedOn?: string | null): string {
  if (status === "terminated") {
    return terminatedOn
      ? `Umowa wypowiedziana — uskutecznione ${fmtDate(terminatedOn)}`
      : "Umowa wypowiedziana";
  }
  return "Wgrano wypowiedzenie umowy — do czasu porozumienia umowa jest w trakcie wypowiedzenia";
}

/**
 * Odznaka statusu przedsięwzięcia, w której los umowy WYPIERA zwykły status:
 * po wgraniu wypowiedzenia zamiast „Aktywne” widać „W trakcie wypowiedzenia”,
 * po porozumieniu — „Wypowiedziana (data)”. Zwykły status wraca dopiero, gdy
 * umowa obowiązuje. Jedno miejsce dla karty przedsięwzięcia, finansów, listy
 * przedsięwzięć i karty klienta — żeby nigdzie nie świeciło „Aktywne” obok
 * wypowiedzianej umowy.
 */
export function ProjectStatusBadge({
  project,
  fallback,
  className,
}: {
  project: Pick<Project, "contract_status" | "contract_terminated_on"> & Partial<Pick<Project, "status" | "is_active">>;
  /** Zwykła etykieta wymuszona przez stronę (lista liczy ją po `is_active`, nie po `status`). */
  fallback?: { label: string; color: "green" | "yellow" | "gray" };
  className?: string;
}) {
  const cs = project.contract_status;
  if (cs === "terminating" || cs === "terminated") {
    return (
      <Badge
        color={cs === "terminated" ? "red" : "orange"}
        className={clsx("gap-1 normal-case", className)}
        title={contractTitle(cs, project.contract_terminated_on)}
      >
        <FileX2 size={12} />
        {CONTRACT_STATUS_LABELS[cs]}
        {cs === "terminated" && project.contract_terminated_on && (
          <span className="font-normal">({fmtDate(project.contract_terminated_on)})</span>
        )}
      </Badge>
    );
  }
  if (!fallback && project.status) {
    const s = project.status as ProjectStatus;
    return <Badge color={PROJECT_STATUS_COLORS[s]} className={className}>{PROJECT_STATUS_LABELS[s]}</Badge>;
  }
  const fb = fallback ?? (project.is_active
    ? { label: "Aktywne", color: "green" as const }
    : { label: "Nieaktywne", color: "gray" as const });
  return <Badge color={fb.color} className={className}>{fb.label}</Badge>;
}

/**
 * Sam znacznik losu umowy (bez zwykłego statusu) — dla nagłówka projektu ZE, gdzie
 * statusem jest status tematu, a nie przedsięwzięcia. Dla umowy obowiązującej
 * nie renderuje nic.
 */
export function ContractStatusBadge({
  status,
  terminatedOn,
  className,
}: {
  status: ProjectContractStatus | null | undefined;
  terminatedOn?: string | null;
  className?: string;
}) {
  if (!status || status === "active") return null;
  const terminated = status === "terminated";
  return (
    <span
      title={contractTitle(status, terminatedOn)}
      data-testid="contract-status-badge"
      className={clsx(
        "inline-flex shrink-0 items-center gap-1 border px-2 py-0.5 font-mono text-[11px] font-bold uppercase tracking-wider",
        terminated
          ? "border-red-600 bg-red-600/10 text-red-700 dark:text-red-400"
          : "border-orange-500 bg-orange-500/10 text-orange-700 dark:text-orange-400",
        className,
      )}
    >
      <FileX2 size={12} />
      {CONTRACT_STATUS_LABELS[status]}
      {terminated && terminatedOn && (
        <span className="font-normal normal-case tracking-normal">({fmtDate(terminatedOn)})</span>
      )}
    </span>
  );
}
